import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PTAFormField from 'Components/PTAFormField'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import TariffImportDialog from 'Services/ProductManagement/Features/Tariff/Components/TariffImportDialog'
import TariffList from 'Services/ProductManagement/Features/Tariff/Components/TariffList'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { useTariffsWithParams } from 'Services/ProductManagement/Features/Tariff/Hooks'
import useTypedSelector from 'Store'
import {
  ENABLE_TARIFF_IMPORT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT
} from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import { useSelectedPta } from 'Util/hooks/useSelectedPta'
import { updateFilterQueryParam } from 'Util/updateFilterQueryParam'
import AddIcon from '@mui/icons-material/Add'
import UploadIcon from '@mui/icons-material/Upload'
import { Box, Switch, Typography } from '@mui/material'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function TariffListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { user } = useTypedSelector(state => state.user)

  const {
    tariffs,
    isFetching,
    totalResults,
    searchQueryParams,
    setSearchQueryParams,
    showPassive,
    setShowPassive
  } = useTariffsWithParams()
  const { setSelectedTariffId } = useContext(TariffContext)
  setSelectedTariffId('')

  const [selectedPta, setSelectedPta] = useState<string | undefined>(undefined)

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`
    }
  ]
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  const handleImportDialogOpen = () => {
    setImportDialogOpen(true)
  }

  const handleImportDialogClose = () => {
    setImportDialogOpen(false)
  }

  useSelectedPta(selectedPta)
  const handlePtaChange = (pta: string | undefined) => {
    setSelectedPta(pta)
    setSearchQueryParams(prev => ({
      ...prev,
      filter: updateFilterQueryParam(prev.filter, 'pta', pta ? `pta eq "${pta}"` : undefined),
      start_index: undefined
    }))
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Tariffs`}</Typography>
        <Box mt={4}>
          <ListActions>
            <Box display="flex" gap={2}>
              <CustomButton
                onClick={() => navigate(`${baseUrl}/create/`)}
                size="small"
                startIcon={<AddIcon />}
              >
                {t`Create new tariff`}
              </CustomButton>
              {ENABLE_TARIFF_IMPORT && (
                <CustomButton
                  onClick={handleImportDialogOpen}
                  size="small"
                  startIcon={<UploadIcon />}
                >
                  {t`Import tariff`}
                </CustomButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  checked={showPassive}
                  onChange={e => setShowPassive(e.target.checked)}
                  slotProps={{ input: { 'aria-label': t`Show passive` } }}
                />
                <Typography variant="body1">{t`Show passive`}</Typography>
              </Box>
              {IS_WALTTI_SERVICE_ENVIRONMENT && isSuperuser(user) && (
                <PTAFormField
                  value={selectedPta}
                  onChange={handlePtaChange}
                  required={false}
                  displayEmptyFirstOption={true}
                />
              )}
              <ListSearch
                searchText={searchText}
                handleSearchTextChange={handleSearchTextChange}
                handleSearch={handleSearch}
                placeholder={t`Search by name or code`}
              />
            </Box>
          </ListActions>
          <TariffList
            rows={tariffs}
            isLoading={isFetching}
            params={searchQueryParams}
            handleRequestSort={handleRequestSort}
            handleChangePage={handleChangePage}
            count={totalResults}
            page={
              searchQueryParams.start_index
                ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                : 0
            }
          />
        </Box>
      </Box>
      {ENABLE_TARIFF_IMPORT && (
        <TariffImportDialog open={importDialogOpen} handleClose={handleImportDialogClose} />
      )}
    </Box>
  )
}

export default TariffListPage
